import { inject, Injector, Service } from '@angular/core';
import { Router } from '@angular/router';
import { Location } from '@core/services/location';
import { Storage } from '@core/services/storage';
import { logging } from '@utils/logging';

@Service()
export class CriticalError {
  private readonly storageService = inject(Storage);
  private readonly locationService = inject(Location);
  private readonly injector = inject(Injector);

  private _panicMode: boolean = false;

  /**
   * Returns true if the application is in panic mode.
   * Panic mode is triggered when a critical error occurs and the application is unable to recover.
   * In panic mode, the application will eventually reload or redirect to the error page.
   * We suppress certain errors in panic mode to avoid unnecessary logging.
   */
  get panicMode(): boolean {
    return this._panicMode;
  }

  /**
   * Triggers panic mode and initiates hard reload of the application. If the application panics 3 times in a row,
   * the user is redirected to the /error page.
   * @param {unknown} error The error that caused the panic.
   */
  panic(error: unknown): void {
    this._panicMode = true;

    // Resolve Router only when needed to avoid RuntimeError: NG0200: Circular dependency found
    const router = this.injector.get(Router);

    if (!this.storageService || !this.locationService || !router) {
      const outerError = new Error('CriticalError.panic: Required services are not available');
      outerError.cause = error;
      logging.error(outerError);
      return;
    }

    // Get the current URL we are currently navigating to or the current URL
    const url = router.currentNavigation()?.initialUrl.toString() ?? router.url;

    const errorLog = this.storageService.pushToCriticalErrorLog(error, url);
    if (errorLog.length >= 3) {
      // If there have been 3 or more critical errors in a row, redirect to the error page
      logging.unknownError(error, { context: 'CriticalError.panic', url: url, errorCount: errorLog.length });
      // TODO: Create a dedicated error page outside of the Angular app
      this.locationService.assign('/error');
    } else {
      // Otherwise, try to reload the page that caused the error
      logging.unknownWarn(error, { context: 'CriticalError.panic', url: url, errorCount: errorLog.length });
      this.locationService.assign(url);
    }
  }
}
